import {
  useSyncExternalStore,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react'
import type { Channel, Lap, Session } from '../xrk/types'
import {
  bestLap,
  buildLapTrace,
  buildProjectedTrace,
  buildReferenceLine,
  deltaTime,
  findSpeedChannel,
  MAX_LAPS,
  traceColor,
  type LapTrace,
  type ReferenceLine,
} from '../analysis/laps'
import { haversine } from '../analysis/lapFilter'
import { resolveSections, sectionAt, type Section } from '../analysis/sections'
import { findTrack, loadTrackRecord, type TrackRecord } from '../lib/tracks'
import ChannelChart, {
  clearAnchor,
  pinnedAt,
  recentlyTouched,
  setPin,
  setSyncCursor,
  subscribePins,
} from './ChannelChart'
import SectionSummary from './SectionSummary'
import AskAiDialog from './AskAiDialog'
import Fullscreen from './Fullscreen'
import ReportRenderer from './ReportRenderer'
import { reportStem } from '../lib/report'
import { saveFiles, shareFiles } from '../lib/share'
import { isNativeApp } from '../lib/native'
import TrackMap from './TrackMap'
import AccelLegend from './AccelLegend'
import TractionCircle from './TractionCircle'
import BikeDialog from './BikeDialog'
import { useBike } from '../lib/bike'
import { tractionForTrace, tractionPeaks, type TraceTraction } from '../analysis/traction'
import { scaleForTraces, type AccelScale } from '../analysis/accel'
import { useIsMobile, useLandscapePhone } from '../lib/useIsMobile'
import type { AccelMode, ViewState } from '../lib/persist'
import { formatDate } from '../lib/dates'

export interface LoadedSession {
  id: string
  name: string
  session: Session
  /** S3 key when the session was opened from the user's repo */
  cloudKey?: string
}

const DEFAULT_CHANNELS = [/^gps[ _]?speed$/i, /^combined lean angle$/i]
const LEAN_CHANNEL = /^combined lean angle$/i

interface Props {
  sessions: LoadedSession[]
  onRemove: (id: string) => void
  /** File buttons and the repo list, rendered at the top of the sidebar */
  sidebar: ReactNode
  sidebarOpen: boolean
  onCloseSidebar: () => void
  busy: boolean
  /** Lap / diagram selection, owned by the parent so it can be persisted and restored */
  view: ViewState
  setView: Dispatch<SetStateAction<ViewState>>
  /** Sessions restored with a saved selection: skip the auto "pick best lap" for these */
  restoredIds: Set<string>
  /** What to call each session in diagrams (the logger's nickname), by file name */
  aliases: Record<string, string>
  /** Toolbar actions: 'png' exports the report image, 'ai' opens the Ask AI dialog */
  exportMode: 'png' | 'ai' | null
  onCloseExport: () => void
  /** Short feedback for the toolbar actions (saved, failed, …) */
  onNotice: (text: string) => void
  /** Switch to the My repo tab, where sessions are opened */
  onOpenRepo: () => void
}

export default function Analysis({
  sessions,
  onRemove,
  sidebar,
  sidebarOpen,
  onCloseSidebar,
  busy,
  view,
  setView,
  restoredIds,
  aliases,
  exportMode,
  onCloseExport,
  onNotice,
  onOpenRepo,
}: Props) {
  const { selected, reference, channelNames, showDelta } = view
  const showSummary = view.showSummary !== false
  const setChannelNames = useCallback((names: string[]) => setView((v) => ({ ...v, channelNames: names })), [setView])
  const setShowDelta = useCallback(
    (f: (cur: boolean) => boolean) => setView((v) => ({ ...v, showDelta: f(v.showDelta) })),
    [setView],
  )
  const toggleSummary = useCallback(() => setView((v) => ({ ...v, showSummary: v.showSummary === false })), [setView])
  const showTraction = view.showTraction !== false
  const toggleTraction = useCallback(
    () => setView((v) => ({ ...v, showTraction: v.showTraction === false })),
    [setView],
  )
  const bike = useBike()
  const [bikeOpen, setBikeOpen] = useState(false)
  const openBike = useCallback(() => setBikeOpen(true), [])
  const closeBike = useCallback(() => setBikeOpen(false), [])
  // The racing lines open with the brake and throttle points on every visit. Full screen can
  // switch the display for the visit; the choice is deliberately not remembered.
  const [accelMode, setAccelMode] = useState<AccelMode>('points')

  // The track record (section boundaries per track) is small and read fresh on every visit.
  const [trackRecord, setTrackRecord] = useState<TrackRecord | null>(null)
  useEffect(() => {
    let live = true
    void loadTrackRecord().then((r) => {
      if (live) setTrackRecord(r)
    })
    return () => {
      live = false
    }
  }, [])
  const [cursor, setCursor] = useState<number | null>(null)
  /** Laps whose lines are hidden on every diagram (a click on a lap's name) */
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const toggleHidden = useCallback((key: string) => {
    setHidden((h) => {
      const next = new Set(h)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  /** Which diagram fills the screen: a channel name, the delta strip, or the map */
  const [full, setFull] = useState<
    | { kind: 'delta' }
    | { kind: 'channel'; name: string }
    | { kind: 'map' }
    | { kind: 'summary' }
    | { kind: 'traction' }
    | null
  >(null)
  const closeFull = useCallback(() => setFull(null), [])
  const onCursor = useCallback((d: number | null) => setCursor(d), [])
  const pinned = useSyncExternalStore(subscribePins, () => pinnedAt('studio') != null)
  const hoverDevice = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  const onMapHover = useCallback((d: number | null) => {
    setSyncCursor('studio', d)
    setCursor(d)
  }, [])
  const seen = useRef<Set<string>>(new Set())
  const mobile = useIsMobile()
  const landscape = useLandscapePhone()
  const chartHeight = landscape ? 200 : mobile ? 220 : 300
  // Sideways phone: the map panel is as tall as the screen allows and the map fills what the
  // title, legend and readout leave over, so the panel never has to scroll.
  const [mapBox, setMapBox] = useState<HTMLDivElement | null>(null)
  const [fitHeight, setFitHeight] = useState(0)
  useEffect(() => {
    if (!landscape || !mapBox) return
    const measure = () => setFitHeight(Math.floor(mapBox.getBoundingClientRect().height))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(mapBox)
    return () => ro.disconnect()
  }, [landscape, mapBox])
  const mapHeight = landscape ? Math.max(100, fitHeight) : mobile ? 320 : 520

  // Union of channel names across sessions, in first-seen order.
  const allChannels = useMemo(() => {
    const out: { name: string; unit: string }[] = []
    const names = new Set<string>()
    for (const s of sessions)
      for (const c of s.session.channels)
        if (!names.has(c.name)) {
          names.add(c.name)
          out.push({ name: c.name, unit: c.unit })
        }
    return out
  }, [sessions])

  // When a session is first loaded, pick its best lap so there is always something to look at.
  useEffect(() => {
    const additions: string[] = []
    for (const s of sessions) {
      if (seen.current.has(s.id)) continue
      seen.current.add(s.id)
      if (restoredIds.has(s.id)) continue
      const best = bestLap(s.session.laps) ?? s.session.laps[0]
      if (best) additions.push(`${s.id}:${best.index}`)
    }
    const ids = new Set(sessions.map((s) => s.id))
    setView((v) => {
      // Drop selections whose session was removed, add the new picks, keep the reference valid.
      const selected = [...v.selected.filter((k) => ids.has(k.split(':')[0])), ...additions]
      const manual = v.referenceManual && v.reference && selected.includes(v.reference)
      const reference = manual ? v.reference : autoReference(sessions, selected)
      const referenceManual = manual ? true : false
      let channelNames = v.channelNames
      if (
        channelNames.length === 0 &&
        allChannels.length > 0 &&
        sessions.length > 0 &&
        !sessions.every((s) => restoredIds.has(s.id))
      ) {
        channelNames = DEFAULT_CHANNELS.map((re) => allChannels.find((c) => re.test(c.name))?.name).filter(
          (n): n is string => !!n,
        )
      }
      if (
        selected.length === v.selected.length &&
        selected.every((k, i) => k === v.selected[i]) &&
        reference === v.reference &&
        referenceManual === !!v.referenceManual &&
        channelNames === v.channelNames
      )
        return v
      return { ...v, selected, reference, referenceManual, channelNames }
    })
  }, [sessions, allChannels, restoredIds, setView])

  const { traces, refTrace, mixedTracks } = useMemo(() => {
    const refLoc = reference ? locate(sessions, reference) : undefined
    const refLine = refLoc ? buildReferenceLine(refLoc.s.session, refLoc.lap) : undefined
    const refSf = refLoc?.s.session.meta.startFinish
    let mixedTracks = false
    const out: LapTrace[] = []
    for (const key of selected) {
      const loc = locate(sessions, key)
      if (!loc) continue
      const sf = loc.s.session.meta.startFinish
      if (refSf && sf && haversine(refSf.lat, refSf.lon, sf.lat, sf.lon) > 500) mixedTracks = true
      out.push({ ...traceFor(loc, refLine, aliases), isReference: key === reference })
    }
    // Laps from different circuits cannot share a distance axis: fall back to each lap's own distance.
    const aligned = mixedTracks
      ? out.map((t) => ({ ...traceFor(locate(sessions, t.key)!, undefined, aliases), isReference: t.isReference }))
      : out
    // Colours go to the non-reference laps in selection order, so the first one is always blue.
    let slot = 0
    const traces = aligned.map((t) => (t.isReference ? t : { ...t, colorSlot: slot++ }))
    const refTrace = reference ? traces.find((t) => t.key === reference) : undefined
    return { traces, refTrace, mixedTracks }
  }, [sessions, selected, reference, aliases])

  // Sections live on the reference lap's distance axis; they need one track and a record for it.
  const trackName = sessions.find((s) => s.session.meta.track)?.session.meta.track
  const sections = useMemo(() => {
    if (!refTrace || mixedTracks || !trackRecord) return []
    return resolveSections(findTrack(trackRecord, trackName), refTrace)
  }, [refTrace, mixedTracks, trackRecord, trackName])
  const sectionsOrNone = sections.length ? sections : undefined
  const accelScale = useMemo(
    () => (accelMode !== 'off' && traces.length ? scaleForTraces(traces).scale : null),
    [accelMode, traces],
  )
  // The summary's rows and lines drive the anchor like a line on a chart: hover follows while
  // nothing is pinned, a click pins the anchor at the middle of the section (again to unpin).
  const onSectionHover = useCallback(
    (s: Section | null) => {
      if (recentlyTouched() || pinnedAt('studio') != null) return
      onMapHover(s ? (s.from + s.to) / 2 : null)
    },
    [onMapHover],
  )
  const onSectionPin = useCallback(
    (s: Section) => {
      const mid = (s.from + s.to) / 2
      if (pinnedAt('studio') === mid) {
        clearAnchor('studio')
        setCursor(null)
        return
      }
      setPin('studio', mid)
      onMapHover(mid)
    },
    [onMapHover],
  )
  const activeSection = sectionAt(sections, cursor)
  const statChannels = useMemo(() => {
    const speed = allChannels.find((c) => DEFAULT_CHANNELS[0].test(c.name))
    const lean = allChannels.find((c) => LEAN_CHANNEL.test(c.name))
    return { speed: speed?.name, speedUnit: speed?.unit, lean: lean?.name }
  }, [allChannels])
  const sectionNote = mixedTracks
    ? 'Sections need laps from one track.'
    : trackRecord && !findTrack(trackRecord, trackName)
      ? `No sections are recorded for ${trackName ?? 'this track'} yet.`
      : undefined
  const unitOf = useMemo(() => new Map(allChannels.map((c) => [c.name, c.unit])), [allChannels])
  // Per-tyre demands of every selected lap, for the traction circle and the report.
  const traction = useMemo(
    () => new Map<string, TraceTraction | null>(traces.map((t) => [t.key, tractionForTrace(t, bike)])),
    [traces, bike],
  )
  const refSession = reference ? locate(sessions, reference)?.s.session : undefined
  const reportCtx = useMemo(() => {
    if (!refTrace) return null
    const ids = [...new Set(traces.map((t) => t.key.split(':')[0]))]
    const files = ids.flatMap((id) => {
      const s = sessions.find((x) => x.id === id)
      return s ? [{ id, name: s.name }] : []
    })
    const laps = showTraction
      ? traces.flatMap((t) => {
          const tr = traction.get(t.key)
          return tr ? [{ label: t.label, peaks: tractionPeaks(tr) }] : []
        })
      : []
    return {
      track: trackName,
      date: refSession?.meta.date,
      traces,
      refTrace,
      sections,
      channels: statChannels,
      sessions: files,
      traction: showTraction ? { bike, laps } : undefined,
    }
  }, [trackName, refSession, traces, refTrace, sections, statChannels, sessions, showTraction, traction, bike])

  const deltaValues = useCallback(
    (t: LapTrace) => (refTrace ? deltaTime(t, refTrace) : new Float64Array(t.distance.length)),
    [refTrace],
  )

  // One stable accessor per channel name; a fresh closure per render would rebuild every chart.
  const accessors = useMemo(() => {
    const m = new Map<string, (t: LapTrace) => Float64Array | undefined>()
    for (const c of allChannels) m.set(c.name, (t) => t.channels[c.name])
    return m
  }, [allChannels])

  function toggleLap(key: string) {
    setView((v) => {
      // No more laps than there are colours to tell them apart.
      if (!v.selected.includes(key) && v.selected.length >= MAX_LAPS) return v
      const selected = v.selected.includes(key) ? v.selected.filter((x) => x !== key) : [...v.selected, key]
      // A hand-picked reference stays while selected; otherwise the best selected lap is the reference.
      const manual = v.referenceManual && v.reference && selected.includes(v.reference)
      const reference = manual ? v.reference : autoReference(sessions, selected)
      return { ...v, selected, reference, referenceManual: manual ? true : false }
    })
  }
  // The everyday diagrams stay at the top of the list; the rest of the logger's channels fold away.
  const pinnedChannels = useMemo(
    () => DEFAULT_CHANNELS.map((re) => allChannels.find((c) => re.test(c.name))).filter((c): c is { name: string; unit: string } => !!c),
    [allChannels],
  )
  const otherChannels = useMemo(() => allChannels.filter((c) => !pinnedChannels.includes(c)), [allChannels, pinnedChannels])
  const [showAllChannels, setShowAllChannels] = useState(false)
  const hiddenSelected = otherChannels.filter((c) => channelNames.includes(c.name)).length
  function toggleChannel(name: string) {
    setChannelNames(channelNames.includes(name) ? channelNames.filter((x) => x !== name) : [...channelNames, name])
  }

  return (
    <div className={`studio-body${sidebarOpen ? ' sidebar-open' : ''}`}>
      {mobile && sidebarOpen && <div className="drawer-backdrop" onClick={onCloseSidebar} />}
      <aside className="studio-side" aria-hidden={!sidebarOpen}>
        <div className="side-inner">
          {sidebar}

          <details open={!mobile || sessions.length <= 1}>
            <summary>
              Opened sessions <span className="count">{selected.length} selected</span>
            </summary>
            {sessions.length === 0 && <p className="muted small">No session open.</p>}
            {sessions.map((s) => {
              const best = bestLap(s.session.laps)
              return (
                <div key={s.id} className="session-block">
                  <h3>
                    <span className="session-name" title={s.name}>
                      {s.name}
                    </span>
                    <button className="link danger" title="Close session" onClick={() => onRemove(s.id)}>
                      ×
                    </button>
                  </h3>
                  <p className="muted small">
                    {s.session.meta.track ?? 'Unknown track'}
                    {s.session.meta.date ? ` · ${formatDate(s.session.meta.date)}` : ''}
                  </p>
                  <div className="table-wrap">
                    <table className="laps">
                      <thead>
                        <tr>
                          <th></th>
                          <th>#</th>
                          <th>Time</th>
                          <th>Δ best</th>
                          <th>Ref</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.session.laps.map((lap) => {
                          const key = `${s.id}:${lap.index}`
                          const sel = selected.indexOf(key)
                          return (
                            <tr
                              key={key}
                              className={lap === best ? 'best' : lap.incomplete ? 'incomplete' : ''}
                              title={lap.reason ? `Excluded from best lap: ${lap.reason}` : undefined}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={sel >= 0}
                                  disabled={sel < 0 && selected.length >= MAX_LAPS}
                                  title={
                                    sel < 0 && selected.length >= MAX_LAPS
                                      ? `Up to ${MAX_LAPS} laps at a time`
                                      : undefined
                                  }
                                  onChange={() => toggleLap(key)}
                                />
                                {sel >= 0 && (
                                  <span
                                    className="swatch"
                                    style={{
                                      background: traceColor(
                                        { isReference: reference === key },
                                        selected.slice(0, sel).filter((k) => k !== reference).length,
                                      ),
                                    }}
                                  />
                                )}
                              </td>
                              <td>{lap.index + 1}</td>
                              <td>{formatTime(lap.duration)}</td>
                              <td>
                                {lap.incomplete ? (
                                  <span className="reason">{lap.reason ?? 'incomplete'}</span>
                                ) : best && lap !== best ? (
                                  `+${(lap.duration - best.duration).toFixed(3)}`
                                ) : (
                                  ''
                                )}
                              </td>
                              <td>
                                <input
                                  type="radio"
                                  name="ref"
                                  checked={reference === key}
                                  onChange={() => setView((v) => ({ ...v, reference: key, referenceManual: true }))}
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </details>

          <details open={!mobile}>
            <summary>
              Diagrams{' '}
              <span className="count">
                {channelNames.length + (showDelta ? 1 : 0) + (showSummary ? 1 : 0) + (showTraction ? 1 : 0)} shown
              </span>
            </summary>
            <ul className="channel-list">
              <li>
                <label>
                  <input type="checkbox" checked={showSummary} onChange={toggleSummary} />
                  Summary <small className="muted">by section</small>
                </label>
              </li>
              <li>
                <label>
                  <input type="checkbox" checked={showDelta} onChange={() => setShowDelta((d) => !d)} />Δ time vs
                  reference <small className="muted">s</small>
                </label>
              </li>
              {pinnedChannels.map((c) => (
                <ChannelRow key={c.name} channel={c} checked={channelNames.includes(c.name)} onToggle={toggleChannel} />
              ))}
              <li>
                <label>
                  <input type="checkbox" checked={showTraction} onChange={toggleTraction} />
                  Traction circle <small className="muted">front / rear, g</small>
                </label>
              </li>
              {otherChannels.length > 0 && (
                <li>
                  <button className="link show-all" onClick={() => setShowAllChannels((x) => !x)}>
                    {showAllChannels ? 'Show fewer' : 'Show all'}
                    {!showAllChannels && hiddenSelected > 0 && (
                      <small className="muted"> {hiddenSelected} more shown</small>
                    )}
                  </button>
                </li>
              )}
              {showAllChannels &&
                otherChannels.map((c) => (
                  <ChannelRow key={c.name} channel={c} checked={channelNames.includes(c.name)} onToggle={toggleChannel} />
                ))}
            </ul>
          </details>
        </div>
      </aside>

      <main className="studio-main">
        {busy && <p className="muted">Working…</p>}
        {sessions.length === 0 && !busy && (
          <p className="muted empty-state">
            No session open.{' '}
            <button className="link" onClick={onOpenRepo}>
              Open one in My repo
            </button>
          </p>
        )}
        {sessions.length > 0 && (
          <div className="analysis-grid">
            <section className="panel charts-panel">
              {traces.length === 0 && <p className="muted">Select at least one lap.</p>}
              {mixedTracks && (
                <p className="warning">
                  The selected laps are from different tracks, so distance cannot be aligned. Each lap is shown on its
                  own distance.
                </p>
              )}
              {traces.length > 0 && showDelta && refTrace && (
                <div className="diagram">
                  <FullscreenButton onClick={() => setFull({ kind: 'delta' })} />
                  <ChannelChart
                    title={`Δ time vs ${refTrace.label}`}
                    unit="s"
                    traces={traces}
                    values={deltaValues}
                    syncKey="studio"
                    height={landscape ? 160 : mobile ? 180 : 220}
                    zeroLine
                    sections={sectionsOrNone}
                    onCursor={onCursor}
                    hidden={hidden}
                    onToggleLap={toggleHidden}
                  />
                </div>
              )}
              {traces.length > 0 &&
                channelNames.map((name) => {
                  const unit = allChannels.find((c) => c.name === name)?.unit ?? ''
                  return (
                    <div key={name} className="diagram">
                      <FullscreenButton onClick={() => setFull({ kind: 'channel', name })} />
                      <ChannelChart
                        title={name}
                        unit={unit}
                        traces={traces}
                        values={accessors.get(name)!}
                        syncKey="studio"
                        height={chartHeight}
                        sections={sectionsOrNone}
                        onCursor={onCursor}
                        hidden={hidden}
                        onToggleLap={toggleHidden}
                      />
                    </div>
                  )
                })}
              {traces.length > 0 && showTraction && (
                <div className="diagram">
                  <FullscreenButton onClick={() => setFull({ kind: 'traction' })} />
                  <h3 className="traction-title">Traction circle</h3>
                  <TractionCircle
                    traces={traces}
                    traction={traction}
                    cursorDistance={cursor}
                    onHover={onMapHover}
                    height={landscape ? 230 : mobile ? 300 : 380}
                    hidden={hidden}
                    onToggleLap={toggleHidden}
                    onAssumptions={openBike}
                  />
                </div>
              )}
              {traces.length > 0 && showSummary && refTrace && (
                <div className="diagram">
                  <FullscreenButton onClick={() => setFull({ kind: 'summary' })} />
                  <SectionSummary
                    traces={traces}
                    refTrace={refTrace}
                    sections={sections}
                    channels={statChannels}
                    active={activeSection}
                    onHover={onSectionHover}
                    onPin={onSectionPin}
                    note={sectionNote}
                  />
                </div>
              )}
            </section>

            <section className="panel map-panel diagram">
              <FullscreenButton onClick={() => setFull({ kind: 'map' })} />
              <h3>Racing lines</h3>
              <div className="map-layout">
                <div className="map-info">
                  <MapLegend
                    traces={traces}
                    cursor={cursor}
                    pinned={pinned}
                    hoverDevice={hoverDevice}
                    accel={accelScale}
                    accelMode={accelMode}
                    hidden={hidden}
                    onToggle={toggleHidden}
                    compact
                  />
                </div>
                <div className="map-box" ref={setMapBox}>
                  <TrackMap
                    traces={traces}
                    cursorDistance={cursor}
                    onHover={onMapHover}
                    height={mapHeight}
                    sections={sectionsOrNone}
                    refTrace={refTrace}
                    accel={accelMode}
                    hidden={hidden}
                  />
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
      {full && traces.length > 0 && (
        <Fullscreen
          title={
            full.kind === 'map'
              ? 'Racing lines'
              : full.kind === 'summary'
                ? 'Summary'
                : full.kind === 'traction'
                  ? 'Traction circle'
                  : full.kind === 'delta'
                    ? `Δ time vs ${refTrace?.label ?? ''}`
                    : full.name
          }
          onClose={closeFull}
          scroll={full.kind === 'summary'}
          actions={
            full.kind === 'map' ? (
              <label className="fullscreen-switch">
                Racing lines
                <AccelModeSelect value={accelMode} onChange={setAccelMode} />
              </label>
            ) : full.kind === 'traction' ? (
              <button className="link fullscreen-switch" onClick={openBike}>
                Assumptions…
              </button>
            ) : undefined
          }
        >
          {({ height }) =>
            full.kind === 'summary' ? (
              refTrace && (
                <SectionSummary
                  traces={traces}
                  refTrace={refTrace}
                  sections={sections}
                  channels={statChannels}
                  active={activeSection}
                  onHover={onSectionHover}
                  onPin={onSectionPin}
                  note={sectionNote}
                />
              )
            ) : full.kind === 'traction' ? (
              <TractionCircle
                traces={traces}
                traction={traction}
                cursorDistance={cursor}
                onHover={onMapHover}
                height={height - 16}
                hidden={hidden}
                onToggleLap={toggleHidden}
                onAssumptions={openBike}
              />
            ) : full.kind === 'map' ? (
              <div className="map-layout full">
                <div className="map-info">
                  <MapLegend
                    traces={traces}
                    cursor={cursor}
                    pinned={pinned}
                    hoverDevice={hoverDevice}
                    accel={accelScale}
                    accelMode={accelMode}
                    hidden={hidden}
                    onToggle={toggleHidden}
                  />
                </div>
                <div className="map-box">
                  <TrackMap
                    traces={traces}
                    cursorDistance={cursor}
                    onHover={onMapHover}
                    height={height - 16}
                    sections={sectionsOrNone}
                    refTrace={refTrace}
                    accel={accelMode}
                    hidden={hidden}
                  />
                </div>
              </div>
            ) : full.kind === 'delta' ? (
              refTrace && (
                <FitChart available={height - 16}>
                  {(h) => (
                    <ChannelChart
                      title={`Δ time vs ${refTrace.label}`}
                      unit="s"
                      traces={traces}
                      values={deltaValues}
                      syncKey="studio"
                      height={h}
                      zeroLine
                      sections={sectionsOrNone}
                      onCursor={onCursor}
                      hidden={hidden}
                      onToggleLap={toggleHidden}
                    />
                  )}
                </FitChart>
              )
            ) : (
              <FitChart available={height - 16}>
                {(h) => (
                  <ChannelChart
                    title={full.name}
                    unit={unitOf.get(full.name) ?? ''}
                    traces={traces}
                    values={accessors.get(full.name)!}
                    syncKey="studio"
                    height={h}
                    sections={sectionsOrNone}
                    onCursor={onCursor}
                    hidden={hidden}
                    onToggleLap={toggleHidden}
                  />
                )}
              </FitChart>
            )
          }
        </Fullscreen>
      )}
      {exportMode === 'ai' && reportCtx && (
        <AskAiDialog
          onClose={onCloseExport}
          ctx={reportCtx}
          showDelta={showDelta}
          accelMode={accelMode}
          showTraction={showTraction}
          channelNames={channelNames}
          units={unitOf}
          accessors={accessors}
          deltaValues={deltaValues}
          note={sectionNote}
        />
      )}
      {exportMode === 'png' && reportCtx && (
        <ReportRenderer
          ctx={reportCtx}
          include={{ summary: true, charts: true, map: true }}
          showDelta={showDelta}
          accelMode={accelMode}
          showTraction={showTraction}
          channelNames={channelNames}
          units={unitOf}
          accessors={accessors}
          deltaValues={deltaValues}
          note={sectionNote}
          onResult={(r) => {
            onCloseExport()
            if ('error' in r) {
              onNotice(`Could not draw the report: ${r.error}`)
              return
            }
            const file = { name: `${reportStem(reportCtx)}.png`, blob: r.png }
            // The phone's share sheet offers Photos, Files, AirDrop…; a browser downloads it.
            const done = isNativeApp ? shareFiles('Mala Racing report', undefined, [file]) : saveFiles([file])
            done.then(
              () => onNotice(isNativeApp ? '' : `Downloaded ${file.name}`),
              (e) =>
                onNotice(
                  /abort|cancel/i.test(String(e)) ? '' : `Export failed: ${e instanceof Error ? e.message : String(e)}`,
                ),
            )
          }}
        />
      )}
      {bikeOpen && <BikeDialog bike={bike} onClose={closeBike} />}
    </div>
  )
}

/**
 * Gives a chart the plot height that makes the whole thing (title, plot, legend) fit the space:
 * uPlot's height is the plot alone, so the rest is measured once drawn and taken off.
 */
function FitChart({ available, children }: { available: number; children: (plotHeight: number) => ReactNode }) {
  const [extras, setExtras] = useState(40)
  const host = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const root = host.current
    if (!root) return
    // The title and the legend are what the plot has to leave room for; their heights do not
    // depend on the plot's, so there is nothing to oscillate.
    const measure = () => {
      let e = 0
      for (const sel of ['.u-title', '.u-legend']) {
        const el = root.querySelector<HTMLElement>(sel)
        if (el) e += el.offsetHeight
      }
      setExtras((cur) => (Math.abs(cur - e) > 1 ? e : cur))
    }
    const ro = new ResizeObserver(measure)
    const watch = () => root.querySelectorAll('.u-title, .u-legend').forEach((el) => ro.observe(el))
    watch()
    measure()
    // The chart appears a tick after the host: watch for it.
    const mo = new MutationObserver(() => {
      watch()
      measure()
    })
    mo.observe(root, { childList: true, subtree: true })
    return () => {
      ro.disconnect()
      mo.disconnect()
    }
  }, [])
  return <div ref={host}>{children(Math.max(80, available - extras))}</div>
}

/** The derived channel the acceleration colouring is read from. */
const ACCEL_CHANNEL = 'GPS Longitudinal Acc'

/** One channel in the diagram list: its checkbox, name and unit. */
function ChannelRow({
  channel,
  checked,
  onToggle,
}: {
  channel: { name: string; unit: string }
  checked: boolean
  onToggle: (name: string) => void
}) {
  return (
    <li>
      <label>
        <input type="checkbox" checked={checked} onChange={() => onToggle(channel.name)} />
        {channel.name} <small className="muted">{channel.unit}</small>
      </label>
    </li>
  )
}

/** The choice of what the racing lines show about acceleration. */
function AccelModeSelect({ value, onChange }: { value: AccelMode; onChange: (m: AccelMode) => void }) {
  return (
    <select
      className="accel-mode"
      value={value}
      onChange={(e) => onChange(e.target.value as AccelMode)}
      aria-label="What the racing lines show"
    >
      <option value="off">Plain</option>
      <option value="lanes">Acceleration lanes</option>
      <option value="points">Brake & throttle points</option>
    </select>
  )
}

function fmtAccel(g: number): string {
  if (!Number.isFinite(g)) return '–'
  return `${g >= 0 ? '+' : '−'}${Math.abs(g).toFixed(2)} g`
}

/** The lap list under the racing lines: colour, lap, time, and the time at the anchor. */
function MapLegend({
  traces,
  cursor,
  pinned,
  hoverDevice,
  accel,
  accelMode = 'lanes',
  compact = false,
  hidden,
  onToggle,
}: {
  traces: LapTrace[]
  cursor: number | null
  pinned: boolean
  hoverDevice: boolean
  accel?: AccelScale | null
  accelMode?: AccelMode
  /** One row per lap (name and times together), so the map keeps more of the panel */
  compact?: boolean
  hidden?: Set<string>
  /** A click on a lap's name shows or hides its line everywhere */
  onToggle?: (key: string) => void
}) {
  return (
    <>
      {accel && <AccelLegend scale={accel} mode={accelMode} />}
      <ul className={`legend${compact ? ' compact' : ''}`}>
        {traces.map((t, i) => (
          <li key={t.key} className={hidden?.has(t.key) ? 'hidden-lap' : ''}>
            <span className="swatch" style={{ background: traceColor(t, i), marginLeft: 0 }} />
            <span className="lap-name" role="button" title="Show or hide this lap" onClick={() => onToggle?.(t.key)}>
              {t.label}
            </span>
            <span className="lap-times">
              <small className="muted">{formatTime(t.lap.duration)}</small>
              {/* Always present, hidden without an anchor, so the legend never changes size. */}
              <span className="muted lap-at" style={{ visibility: cursor != null ? 'visible' : 'hidden' }}>
                {' · '}
                {cursor != null ? formatTime(valueAt(t.distance, t.time, cursor)) : formatTime(t.lap.duration)}
                {/* With the acceleration colouring on, the reading at the anchor too */}
                {accel && !compact && t.channels[ACCEL_CHANNEL] && (
                  <span className="lap-accel">
                    {' · '}
                    {cursor != null ? fmtAccel(valueAt(t.distance, t.channels[ACCEL_CHANNEL], cursor)) : '+0.00 g'}
                  </span>
                )}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="cursor-line">
        {cursor != null ? (
          <p className="muted cursor-readout">
            {Math.round(cursor)} m from lap start
            {pinned ? (hoverDevice ? ' · pinned (click empty space or Esc to clear)' : ' · pinned') : ''}
          </p>
        ) : (
          <p className="muted cursor-hint">
            {hoverDevice ? 'Hover a line to read it, click it to pin' : 'Tap a line to read it'}
          </p>
        )}
      </div>
    </>
  )
}

/** The expand icon in a diagram's top-right corner. */
function FullscreenButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="fullscreen-button" title="Full screen" aria-label="Full screen" onClick={onClick}>
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
      </svg>
    </button>
  )
}

interface Located {
  s: LoadedSession
  lap: Lap
  speed: Channel
}

function locate(sessions: LoadedSession[], key: string): Located | undefined {
  const [sid, idxStr] = key.split(':')
  const s = sessions.find((x) => x.id === sid)
  if (!s) return undefined
  const lap = s.session.laps.find((l) => l.index === Number(idxStr))
  const speed = findSpeedChannel(s.session)
  if (!lap || !speed) return undefined
  return { s, lap, speed }
}

function traceFor(
  { s, lap, speed }: Located,
  line: ReferenceLine | undefined,
  aliases: Record<string, string>,
): LapTrace {
  // Diagrams name a lap after the logger's nickname when there is one, else after the file.
  const ids = { sessionId: s.id, sessionName: aliases[s.name] || s.name.replace(/\.(xrk|xrz|hrz)$/i, '') }
  return line ? buildProjectedTrace(s.session, lap, line, speed, 2, ids) : buildLapTrace(s.session, lap, speed, 2, ids)
}

/** Best complete lap among the selected ones; falls back to the fastest selected lap of any kind. */
function autoReference(sessions: LoadedSession[], selected: string[]): string | undefined {
  let best: { key: string; d: number; complete: boolean } | undefined
  for (const key of selected) {
    const loc = locate(sessions, key)
    if (!loc) continue
    const cand = { key, d: loc.lap.duration, complete: !loc.lap.incomplete }
    if (!best || (cand.complete && !best.complete) || (cand.complete === best.complete && cand.d < best.d)) best = cand
  }
  return best?.key
}

function valueAt(xs: Float64Array, ys: Float64Array, x: number): number {
  if (x <= xs[0]) return ys[0]
  const last = xs.length - 1
  if (x >= xs[last]) return ys[last]
  let lo = 0
  let hi = last
  while (hi - lo > 1) {
    const mid = (lo + hi) >>> 1
    if (xs[mid] <= x) lo = mid
    else hi = mid
  }
  const f = (x - xs[lo]) / (xs[hi] - xs[lo])
  return ys[lo] + f * (ys[hi] - ys[lo])
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return '–'
  const m = Math.floor(seconds / 60)
  const s = seconds - m * 60
  return `${m}:${s.toFixed(3).padStart(6, '0')}`
}
